import React, { useContext, useEffect, useState } from "react";
import { Button, Container, Drawer, Flex, Heading, Input, Divider, Plus, Text, TrashSimple } from "@cisco/spaces-dash-lib-styleguide";
import { I18nContext } from "../../contexts/i18nContext";
import { listApPrefix, searchMatchingApPrefix, updateApPrefix } from "../../service/business-hierarchy-service";
import useLoader from "../../hooks/useLoader";
import useNotification from "../../hooks/useNotification";

function LocationEditPrefixDrawer(props) {
  const { t } = useContext(I18nContext);
  const { isOpen, onClose, nodeId, nodeName, selectedAps, setSelectedAps, getApPrefixList } = props;
  const { setLoading } = useLoader();
  const { setToastMessage } = useNotification();

  const [searchText, setSearchText] = useState("");
  const [filteredApList, setFilteredApList] = useState(null);

  const checkApExists = (apName) => {
    let exists = false;
    if (selectedAps && selectedAps.length) {
      selectedAps.forEach((ap) => {
        if (ap.key.toLowerCase() === apName.toLowerCase()) {
          exists = true;
          setToastMessage(t("location-info.prefix-added-in-list"), 5000, "negative");
        }
      });
    }
    return exists;
  };

  const getAddedAPs = () => {
    if (!selectedAps || selectedAps.length === 0) return [];
    return selectedAps.map((ap) => ap.key);
  };

  const addPrefix = () => {
    if (searchText && !checkApExists(searchText)) {
      const payload = {
        locationId: nodeId,
        type: "addprefix",
        newPrefix: searchText,
        addedPrefixes: getAddedAPs()
      };
      setLoading(true);
      listApPrefix(payload).then(
        (res) => {
          setLoading(false);
          if (res.status && res.data) {
            setSearchText("");
            setFilteredApList(null);
            if (res.data.apPrefixCount && Object.keys(res.data.apPrefixCount)) {
              const tempSelectedAps = [];
              Object.keys(res.data.apPrefixCount).forEach((ap) => {
                tempSelectedAps.push({
                  key: ap,
                  count: res.data.apPrefixCount[ap] ? res.data.apPrefixCount[ap].length : 0
                });
              });
              setSelectedAps(() => tempSelectedAps);
            }
          } else {
            onClose();
            setToastMessage(t("location-info.error-on-adding-ap-prefix"), 5000, "negative");
          }
        },
        () => {
          setLoading(false);
          onClose();
          setToastMessage(t("location-info.error-on-adding-ap-prefix"), 5000, "negative");
        }
      );
    }
  };

  const deleteApPrefix = (index) => {
    setSelectedAps((prev) => {
      const temp = [...prev];
      temp.splice(index, 1);
      return temp;
    });
  };

  const saveApPrefix = () => {
    const payload = {
      locationId: nodeId,
      networkName: nodeName,
      apNamePrefixes: getAddedAPs(),
      type: "WLC_NODE"
    };
    setLoading(true);
    updateApPrefix(payload).then(
      (data) => {
        setLoading(false);
        if (data.status && data.data) {
          onClose();
          setToastMessage(data.message, 5000, "positive");
          getApPrefixList();
        } else {
          setToastMessage(t("location-info.error-updating-ap-prefix"), 5000, "negative");
        }
      },
      () => {
        setLoading(false);
        setToastMessage(t("location-info.error-updating-ap-prefix"), 5000, "negative");
      }
    );
  };

  useEffect(() => {
    if (!searchText || searchText === "") {
      setFilteredApList(null);
      return;
    }
    if (!nodeId) return;
    const payload = {
      currentLocationId: nodeId,
      type: "searchPrefixes",
      apNamePrefix: searchText
    };

    setLoading(true);
    searchMatchingApPrefix(payload)
      .then((res) => {
        setLoading(false);
        if (res.status && res.data) {
          setFilteredApList(res.data.apLocation);
        } else {
          setFilteredApList([]);
        }
      })
      .catch(() => {
        setLoading(false);
        setFilteredApList([]);
      });
  }, [searchText]);

  return (
    <Drawer isOpen={isOpen} size="xl" onClose={onClose}>
      <Drawer.Heading>
        <Heading>
          {t("hierarchy-nodes.add-edit-prefix")}: {nodeName}
        </Heading>
      </Drawer.Heading>
      <Drawer.Content>
        <Flex direction="vertical" gap={16}>
          <Text>{t("location-info.provide-one-or-more-prefix")}</Text>
          <Flex direction="horizontal" gap={12}>
            <Container.Well style={{ padding: "0px", width: "50%" }}>
              <Flex style={{ width: "100%", height: "400px", padding: "16px" }} direction="vertical" gap={12}>
                <Text weight="bold">{t("location-info.prefix")}</Text>
                <Input
                  placeholder={t("location-info.enter-prefix")}
                  suffix={
                    <Input.FunctionalButton style={{ width: "45%" }} onClick={addPrefix}>
                      <Button icon={<Plus size={16} weight="bold" />} kind="tertiary">
                        {t("location-info.add-prefix-text")}
                      </Button>
                    </Input.FunctionalButton>
                  }
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {filteredApList !== null && searchText !== "" && (
                  <Flex direction="vertical" gap={4}>
                    {filteredApList.length === 0 ? (
                      <Text>
                        {t("location-info.no-access-points-match")} &quot;{searchText}&quot;
                      </Text>
                    ) : (
                      <Text>
                        {filteredApList.length} {t("location-info.access-point-match-prefix")} &quot;{searchText}&quot;
                      </Text>
                    )}
                    {filteredApList.length > 0 && (
                      <Flex direction="vertical" style={{ height: "250px", overflow: "auto" }}>
                        {filteredApList.map((ap) => (
                          <Flex direction="vertical" key={ap.apName}>
                            <Divider />
                            <Flex justify="space-between">
                              <Text>{ap.apName}</Text>
                              <Text>{ap.locationName}</Text>
                            </Flex>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Flex>
                )}
              </Flex>
            </Container.Well>
            <Container.Well style={{ padding: "0px", width: "50%" }}>
              <Flex style={{ width: "100%", height: "400px", padding: "16px" }} direction="vertical" gap={12}>
                <Text weight="bold">{t("location-info.existing-prefixes")}</Text>
                <Text size="p4">{t("location-info.already-prefixed")}</Text>
                {selectedAps.map((ap, i) => (
                  <Container key={ap.key} style={{ padding: "16px" }}>
                    <Flex direction="horizontal" gap={4} align="center">
                      <Flex direction="vertical" gap={4} grow>
                        <Text weight="semi-bold">{ap.key}</Text>
                        <Text size="p4">
                          {ap.count} {t("hierarchy-nodes.aps-text")}
                        </Text>
                      </Flex>
                      <Button onClick={() => deleteApPrefix(i)} kind="tertiary" icon={<TrashSimple size={16} />} />
                    </Flex>
                  </Container>
                ))}
              </Flex>
            </Container.Well>
          </Flex>
        </Flex>
      </Drawer.Content>
      <Flex direction="horizontal" justify="end">
        <Button.Group>
          <Button kind="tertiary" onClick={onClose}>
            {t("hierarchy-nodes.cancel-text")}
          </Button>
          <Button onClick={saveApPrefix}>{t("hierarchy-nodes.save-text")}</Button>
        </Button.Group>
      </Flex>
    </Drawer>
  );
}

export default LocationEditPrefixDrawer;
